"use client";

import { useDevRev } from "@/components/DevRevProvider";
import { RiveAnimation } from "@/components/layout/RiveAnimation";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  Sidebar as SidebarPrimitive,
} from "@/components/ui/sidebar";
import { NAVIGATION_ITEMS } from "@/constants/navigation";
import { Action } from "@/context/AnalyticsProvider";
import { useNavigationAnalytics } from "@/context/AnalyticsProvider/navigation";
import { useActiveRoute } from "@/hooks/useActiveRoute";
import type { NavigationItem, RiveControlRef } from "@/types/navigation";
import { HelpCircle, Users } from "lucide-react";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface MenuItemProps {
  item: NavigationItem;
  index: number;
}

function MenuItem({ item }: MenuItemProps) {
  const { track } = useNavigationAnalytics();
  const active = useActiveRoute(item.url);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (item.action) {
        e.preventDefault();
        item.action();
      }
      track({
        action: Action.PRESSED,
        extras: {
          label: item.title,
          section: "LeftNav",
          url: item.url,
        },
      });
    },
    [item, track],
  );

  return (
    <SidebarMenuItem
      key={item.title}
      className="animate-in fade-in slide-in-from-bottom-2 duration-300"
    >
      <SidebarMenuButton
        asChild
        size="lg"
        variant="navigation"
        isActive={active}
      >
        <Link
          href={item.url}
          onClick={handleClick}
          className="navigation-link menu-item-link flex items-center gap-3 w-full h-12 group-data-[collapsible=icon]:justify-center hover:!text-white relative overflow-hidden [&:hover_svg]:scale-110"
        >
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 relative">
            <item.icon
              className={`transition-all duration-300 ${
                active
                  ? "text-purple-300 drop-shadow-[0_0_12px_rgba(255,255,255,0.8)] drop-shadow-[0_0_24px_rgba(139,92,246,1)]"
                  : "drop-shadow-[0_0_8px_rgba(255,255,255,0.6)] drop-shadow-[0_0_16px_rgba(103,126,234,0.8)]"
              }`}
            />
          </div>
          <div className="flex items-center gap-2 transition-opacity duration-150 group-data-[state=collapsed]:opacity-0 group-data-[state=collapsed]:duration-100 group-data-[state=expanded]:delay-100 group-data-[collapsible=icon]:hidden">
            <span
              className={`text-sm text-white transition-all duration-300 ${
                active ? "font-bold" : ""
              }`}
            >
              {item.title}
            </span>
            {item.badge && <Badge variant="default">{item.badge}</Badge>}
          </div>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function LeftNav() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { openWidget } = useDevRev();
  const riveRef = useRef<RiveControlRef>(null);
  const [isPermanentOpenLeftNav, setIsPermanentOpenLeftNav] = useState(false);

  useEffect(() => {
    setSidebarOpen(isPermanentOpenLeftNav);
  }, [isPermanentOpenLeftNav]);

  const handleTogglePermanentOpen = useCallback(() => {
    const newPermanentState = !isPermanentOpenLeftNav;
    setIsPermanentOpenLeftNav(newPermanentState);
    setSidebarOpen(newPermanentState);

    if (!newPermanentState) {
      riveRef.current?.reset();
      riveRef.current?.reverse();
    }
  }, [isPermanentOpenLeftNav]);

  const handleMouseEnter = useCallback(() => {
    if (!isPermanentOpenLeftNav) {
      riveRef.current?.play();
    }
  }, [isPermanentOpenLeftNav]);

  const navItems = useMemo(() => {
    return NAVIGATION_ITEMS.filter((item) => item.title !== "Referral");
  }, []);

  const navigationSection = useMemo(
    () =>
      navItems.map((item, index) => (
        <MenuItem key={item.title} item={item} index={index} />
      )),
    [navItems],
  );

  return (
    <SidebarPrimitive
      collapsible="icon"
      open={sidebarOpen}
      onOpenChange={setSidebarOpen}
      onMouseEnter={handleMouseEnter}
      className="border-r border-white/20 bg-transparent backdrop-blur-md shadow-xl [&_[data-sidebar=sidebar]]:bg-transparent"
    >
      <SidebarHeader className="p-0 border-b border-white/20 bg-transparent backdrop-blur-sm">
        <div
          className={`flex items-center transition-all duration-300 ${sidebarOpen ? "gap-1 p-3" : "justify-center p-3"}`}
        >
          <Button
            variant="ghost"
            size="icon"
            className={`focus:outline-none focus-visible:outline-none focus-visible:ring-0 ${sidebarOpen ? "text-white/80 hover:bg-transparent hover:text-white/80 h-12 w-12 p-2 transition-all duration-300" : "h-10 w-10 text-white/60 hover:text-white hover:bg-white/10 transition-none p-0 flex items-center justify-center !rounded-full mx-auto my-1 flex-shrink-0"}`}
            onClick={handleTogglePermanentOpen}
          >
            <RiveAnimation
              ref={riveRef}
              src="/rive/hamburger-icon.riv"
              className="w-7 h-7 transition-all duration-300"
            />
            <span className="sr-only">Toggle Sidebar</span>
          </Button>
          {sidebarOpen && (
            <div className="text-lg font-bold text-white/90 drop-shadow-lg transition-opacity duration-150 select-none whitespace-nowrap mr-8 group-data-[state=collapsed]:opacity-0 group-data-[state=collapsed]:duration-100 group-data-[state=expanded]:delay-100">
              Immutable Play
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="flex flex-col h-full bg-transparent">
        <SidebarGroup className="bg-transparent">
          <SidebarGroupContent className="bg-transparent">
            <SidebarMenu className="space-y-1 py-2">
              {navigationSection}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <div className="flex-1" />

        <SidebarGroup className="pb-6 bg-transparent whitespace-nowrap">
          <SidebarGroupContent className="bg-transparent">
            <SidebarMenu className="space-y-1">
              <MenuItem
                item={{
                  title: "Referral",
                  url: "/referral",
                  icon: Users,
                }}
                index={0}
              />
              <MenuItem
                item={{
                  title: "Help",
                  url: "#help",
                  icon: HelpCircle,
                  action: openWidget,
                }}
                index={1}
              />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </SidebarPrimitive>
  );
}
